import { Box, Heading, Icon, Text, HStack, Label } from '@medely/ui-kit';
import { useGate } from '@mp/hooks';
import { gateNames } from '@mp/constants';
import React from 'react';
import { JobDetailsFragment } from '../graphql';
import { FragmentType, parseFragmentData } from '@mp/graphql/generated';

interface ClinicalInstructionsProps {
  job?: FragmentType<typeof JobDetailsFragment> | null;
  smallIcon?: boolean;
}

export const ClinicalInstructions = ({
  job: inputJob,
  smallIcon = false,
}: ClinicalInstructionsProps) => {
  const job = parseFragmentData(JobDetailsFragment, inputJob);

  const isFacilityGuideEnabled = useGate(gateNames.facilityGuide);

  if (!isFacilityGuideEnabled) {
    return null;
  }

  const bookedOrInProgress = ['booked', 'in_progress', 'completed', 'clocked_in'].includes(
    job?.status ?? '',
  );

  const ehrSystem = job?.location?.ehr_system?.label;
  const ehrDetails = job?.location?.ehr_details;
  const medicationManagementDetails = job?.location?.medication_management_details;
  const suppliesPpeDetails = job?.location?.supplies_ppe_details;

  // Don't show if no data available
  const hasData = ehrSystem || ehrDetails || medicationManagementDetails || suppliesPpeDetails;
  if (!hasData) {
    return null;
  }

  return (
    <Box testId="clinical-instructions" mb={3}>
      <Heading size="s" color="text.primary" testId="clinical-instructions-title">
        Clinical instructions
      </Heading>

      {/* Clinical charting section */}
      {(ehrSystem || ehrDetails) && (
        <Box mt={smallIcon ? 1 : 3} testId="clinical-charting-section">
          <HStack alignItems="center">
            <Icon
              name="light-clipboard-medical"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
            <Box ml={1}>
              <Label size="l" color="text.primary" testId="meal-breaks-heading">
                Clinical charting
              </Label>
            </Box>
          </HStack>

          {ehrSystem && (
            <Box ml={4}>
              <Text size="m" color="text.primary" testId="ehr-system">
                • EHR: {ehrSystem}
              </Text>
            </Box>
          )}

          {ehrDetails && bookedOrInProgress && (
            <Box ml={4}>
              <Text size="m" color="text.primary" testId="ehr-details">
                • {ehrDetails}
              </Text>
            </Box>
          )}
        </Box>
      )}

      {/* Medication management section - only for booked/in progress */}
      {medicationManagementDetails && bookedOrInProgress && (
        <Box mt={1} testId="medication-management-section">
          <HStack alignItems="center">
            <Icon
              name="light-prescription"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
            <Box ml={1}>
              <Label size="l" color="text.primary" testId="meal-breaks-heading">
                Medication management
              </Label>
            </Box>
          </HStack>
          <Box ml={4}>
            <Text size="m" color="text.primary" testId="medication-details">
              {medicationManagementDetails}
            </Text>
          </Box>
        </Box>
      )}

      {/* Supplies and PPE section - only for booked/in progress */}
      {suppliesPpeDetails && bookedOrInProgress && (
        <Box mt={1} testId="supplies-ppe-section">
          <HStack alignItems="center">
            <Icon
              name="light-stethoscope"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
            <Box ml={1}>
              <Label size="l" color="text.primary" testId="supplies-heading">
                Supplies and PPE
              </Label>
            </Box>
          </HStack>
          <Box ml={4} mb={1}>
            <Text size="m" color="text.primary" testId="supplies-details">
              {suppliesPpeDetails}
            </Text>
          </Box>
        </Box>
      )}
    </Box>
  );
};
